import formatPrice from '@utils/shared/format/formatPrice';
import React from 'react';
import { SaleOnSalePrice, VendorTieredDiscountCodeModel } from '@codegen-basetypes';
import { IPrice } from '@models/VendorVariant/i-vendor-variant';

import styles from './RegularSalePriceDisplay.module.scss';

interface RegularSalePriceDisplayProps {
  product: {
    saleOnSalePrice?: SaleOnSalePrice | null;
    vendorTieredDiscountCode?: VendorTieredDiscountCodeModel | null;
    price?: number | string;
    currentPrice?: number | string | null;
    retailPrice?: IPrice | null;
  };
  className?: string;
  needsSmallerFont?: boolean;
  tieredDiscountTwoLine?: boolean;
}

export default function RegularSalePriceDisplay({
  product,
  className,
  needsSmallerFont = false,
  tieredDiscountTwoLine = false
}: RegularSalePriceDisplayProps) {
  const {
    price,
    retailPrice,
    currentPrice,
    vendorTieredDiscountCode
  } = product;

  const priceText = price || currentPrice;

  const isOnSale = !!vendorTieredDiscountCode || Number(priceText) !== retailPrice?.price;

  return (
    <div className={`${className} ${styles.RegularSalePriceDisplay}`}>
      {isOnSale ? (
        <SaleDisplay
          product={product}
          needsSmallerFont={needsSmallerFont}
          tieredDiscountTwoLine={tieredDiscountTwoLine}
        />
      ) : (
        <span className={styles.NotSalePrice}>{formatPrice(priceText)}</span>
      )}
    </div>
  );
}

function SaleDisplay({
  product,
  needsSmallerFont,
  tieredDiscountTwoLine
}: {
  product: {
    saleOnSalePrice?: SaleOnSalePrice | null;
    vendorTieredDiscountCode?: VendorTieredDiscountCodeModel | null;

    price?: number | string;
    currentPrice?: number | string | null;
    retailPrice?: IPrice | null;
  };

  needsSmallerFont?: boolean;
  tieredDiscountTwoLine?: boolean;
}) {
  const {
    saleOnSalePrice,
    vendorTieredDiscountCode,
    currentPrice,
    price,
    retailPrice
  } = product;
  const isOnSale = Number(currentPrice) !== retailPrice?.price;
  const isSaleOnSale = !!saleOnSalePrice;
  const isTieredDiscount = !!vendorTieredDiscountCode;
  const priceText = price || currentPrice;
  if (isSaleOnSale) {
    return (
      <div
        className={`${needsSmallerFont ? styles.NeedsSmallerFont : ''}`}
      >
        <div className={styles.RegularPrice}>
          <span className={styles.OriginalPrice}>{formatPrice(retailPrice?.price)}</span>
          &nbsp;
          <span className={styles.DiscountedPrice}>{formatPrice(saleOnSalePrice?.price)}</span>
        </div>
        <div className={styles.ExtraCopy}>{getCopy(product)}</div>
      </div>
    );
  }

  if (isTieredDiscount) {
    return (
      <div
        className={`${needsSmallerFont ? styles.NeedsSmallerFont : ''}`}
      >
        <div className={styles.RegularPrice}>
          <span className={styles.OriginalPrice}>{formatPrice(retailPrice?.price)}</span>
          &nbsp;
          <span className={`${styles.TieredPrice} ${tieredDiscountTwoLine ? styles.TwoLine : ''}`}>Discount Applied in Cart</span>
        </div>
        <div className={styles.ExtraCopy}>{getCopy(product)}</div>
      </div>
    );
  }
  if (isOnSale) {
    return (
      <div
        className={`${needsSmallerFont ? styles.NeedsSmallerFont : ''}`}
      >
        <div className={styles.RegularPrice}>
          <span className={styles.OriginalPrice}>{formatPrice(retailPrice?.price)}</span>
          &nbsp;
          <span className={styles.DiscountedPrice}>{formatPrice(priceText)}</span>
        </div>
        <div className={styles.ExtraCopy}>{getCopy(product)}</div>
      </div>
    );
  }

  return null;
}

function getCopy({
  price,
  saleOnSalePrice,
  vendorTieredDiscountCode,
  currentPrice,
  retailPrice
}: {
  price?: number | string;
  saleOnSalePrice?: SaleOnSalePrice | null;
  vendorTieredDiscountCode?: VendorTieredDiscountCodeModel | null;
  currentPrice?: number | string | null;
  retailPrice?: IPrice | null;
}) {
  const priceText = price || currentPrice;
  if (saleOnSalePrice) {
    return 'Exclusive Savings | Price as Marked';
  } else if (vendorTieredDiscountCode) {
    return vendorTieredDiscountCode.discountText ?? `Buy More, Save More up to ${vendorTieredDiscountCode.maxSavingsPercentage}%`;
  } else if (priceText && retailPrice?.price && (Number(priceText) < retailPrice?.price)) {
    return 'Limited Time Offer | Price as Marked';
  }

  return '';
}
